<template>
  <div>
    <!-- Modal -->
    <div 
      id="payment" 
      class="modal fade show"  
      tabindex="-1" 
      role="dialog" 
      aria-labelledby="paymentModalLabel" 
      aria-hidden="false"
      style="display: block;"
    >
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header text-white">
            <h5 class="modal-title" id="paymentModalLabel">PAYMENT PROCESSING</h5>
            <button type="button" class="close text-white" @click="closeModal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          
          <div class="modal-body">
            <!-- Total Amount Section -->
            <div class="total-section mb-4 p-3 bg-light rounded">
              <div class="d-flex justify-content-between align-items-center">
                <h6 style="color: #45a049;" class="mb-0"> TOTAL</h6>
                <h4 style="color: #45a049;" class="mb-0 font-weight-bold">{{ formatCurrency(grandTotal) }} AED</h4>
              </div>
            </div>
            
            <!-- Payment Inputs -->
            <div class="payment-inputs mb-4">
              <div class="row mb-3">
                <!-- Cash Amount Input -->
                <div class="col-md-6">
                  <label for="cash-amount" class="form-label">Cash Amount (AED)</label>
                  <div class="input-group">
                    <input 
                      id="cash-amount" 
                      v-model="cashAmount"
                      class="form-control" 
                      type="text" 
                      placeholder="0.00"
                      @input="calculateBalance"
                      @click="openNumericKeypad('cashAmount')"
                      readonly
                    />
                    <button class="btn btn-outline-secondary" @click="clearInput('cashAmount')">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
                
                <!-- Credit Amount Input -->
                <div class="col-md-6">
                  <label for="credit-amount" class="form-label">Credit Amount (AED)</label>
                  <div class="input-group">
                    <input 
                      id="credit-amount" 
                      v-model="creditAmount"
                      class="form-control" 
                      type="text" 
                      placeholder="0.00"
                      @input="calculateBalance"
                      @click="openNumericKeypad('creditAmount')"
                      readonly
                    />
                    <button class="btn btn-outline-secondary" @click="clearInput('creditAmount')">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Customer Account Section -->
            <div class="customer-account mb-4">
              <h6 class="section-title mb-3">CUSTOMER ACCOUNT (OPTIONAL)</h6>
              <div class="row">
                <div class="col-md-12">
                  <label for="account-search" class="form-label">Search Customer</label>
                  <div class="input-group">
                    <span class="input-group-text"><i class="fas fa-search"></i></span>
                    <input 
                      type="text" 
                      class="form-control"
                      id="account-search"
                      v-model="accountSearchQuery"
                      @input="filterAccountCustomers"
                      @focus="showAccountResults = true"
                      @blur="handleAccountInputBlur"
                      placeholder="Search by phone or name"
                    />
                    <button class="btn btn-outline-secondary" @click="clearAccountSearch">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  
                  <!-- Customer Dropdown Results -->
                  <div class="dropdown-results mt-1" v-show="showAccountResults && accountSearchQuery">
                    <div class="list-group">
                      <div 
                        v-for="customer in filteredAccountCustomers" 
                        :key="customer.id" 
                        class="list-group-item list-group-item-action"
                        @mousedown.prevent="selectAccountCustomer(customer)"
                      >
                        <div class="d-flex justify-content-between">
                          <div>
                            <strong>{{ customer.phone }}</strong>
                            <span class="ms-2">{{ customer.name }}</span>
                          </div>
                          <span :class="{'text-success': customer.balance > 0, 'text-danger': customer.balance < 0}">
                            {{ formatCurrency(customer.balance) }} AED
                          </span>
                        </div>
                      </div>
                      <div v-if="filteredAccountCustomers.length === 0" class="list-group-item text-muted">
                        No customers found
                      </div>
                    </div>
                  </div>
                  
                  <!-- Selected Customer Info -->
                  <div v-if="selectedAccountCustomer" class="customer-info mt-3 p-3 rounded" 
                       :class="{'bg-success-light': selectedAccountCustomer.balance > 0, 'bg-danger-light': selectedAccountCustomer.balance < 0}">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{{ selectedAccountCustomer.name }}</strong>
                        <div class="text-muted small">{{ selectedAccountCustomer.phone }}</div>
                      </div>
                      <div class="text-end">
                        <div class="small">Current Balance</div>
                        <strong :class="{'text-success': selectedAccountCustomer.balance > 0, 'text-danger': selectedAccountCustomer.balance < 0}">
                          {{ formatCurrency(selectedAccountCustomer.balance) }} AED
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Payment Summary -->
            <div class="payment-summary mb-4 p-3 rounded border">
              <div class="row mb-2">
                <div class="col-md-6">
                  <label for="previous_pay" class="form-label">Previous Payment (AED)</label>
                  <div class="input-group">
                    <input 
                      id="previous_pay" 
                      v-model="previousPayment"
                      class="form-control" 
                      type="text" 
                      placeholder="0.00"
                      @input="calculateBalance"
                      @click="openNumericKeypad('previousPayment')"
                      readonly
                    />
                    <button class="btn btn-outline-secondary" @click="clearInput('previousPayment')">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
                <div class="col-md-6">
                  <label for="paid-amount" class="form-label">Paid Amount (AED)</label>
                  <div class="input-group">
                    <input 
                      id="paid-amount" 
                      v-model="paidAmount"
                      class="form-control" 
                      type="text" 
                      placeholder="0.00"
                      readonly
                    />
                  </div>
                </div>
              </div>
              
              <div class="row">
                <div class="col-md-12">
                  <label for="balance" class="form-label">Balance (AED)</label>
                  <div class="input-group">
                    <input 
                      id="balance" 
                      v-model="balance"
                      class="form-control font-weight-bold" 
                      :class="{'text-success': parseFloat(balance) >= 0, 'text-danger': parseFloat(balance) < 0}"
                      type="text" 
                      placeholder="0.00"
                      readonly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Modal Footer -->
          <div class="modal-footer">
            <button class="btn btn-lg btn-outline-secondary" @click="closeModal">
              <i class="fas fa-times mr-2"></i> Cancel
            </button>
            <button class="btn btn-lg submit-btn" @click="processPayment" :disabled="parseFloat(balance) > 0">
              <i class="fas fa-check-circle mr-2"></i> Complete Payment
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade show"></div>
    
    <!-- Numeric Keypad Component -->
    <NumericKeypad 
      ref="numericKeypad"
      @input="handleKeypadInput"
      @close="closeKeypad"
    />
  </div>
</template>

<script>
import NumericKeypad from './NumericKeypad.vue';

export default {
  name: 'PaymentModal',
  components: {
    NumericKeypad
  },
  props: {
    grandTotal: {
      type: Number,
      required: true
    },
    orderId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      cashAmount: '',
      creditAmount: '',
      previousPayment: '',
      paidAmount: 0,
      balance: 0,
      accountSearchQuery: '',
      filteredAccountCustomers: [],
      showAccountResults: false,
      selectedAccountCustomer: null,
      customers: [],
      loadingCustomers: false,
      showKeypad: false,
      currentInputField: null
    }
  },
  watch: {
    selectedAccountCustomer() {
      this.applyCustomerBalance();
    },
    cashAmount() {
      this.calculateBalance();
    },
    creditAmount() {
      this.calculateBalance();
    },
    previousPayment() {
      this.calculateBalance();
    }
  },
  async mounted() {
    await this.fetchCustomers();
    this.filteredCustomers = [...this.customers];
  },
  methods: {
openNumericKeypad(field) {
      this.currentInputField = field;
      // Set initial value
      this.$refs.numericKeypad.inputValue = this[field] || '';
      // Show the keypad
      this.$refs.numericKeypad.show();
    },
  
    handleKeypadInput(value) {
      if (this.currentInputField) {
        this[this.currentInputField] = value;
        this.calculateBalance();
      }
    },
  
  closeKeypad() {
    this.showKeypad = false;
    this.currentInputField = null;
  },
      setInputValue(value) {
    this.inputValue = value;
  },
    async fetchCustomers() {
      this.loadingCustomers = true;
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${this.$BASE_API_URL}/api/customers`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch customers');
        }

        const data = await response.json();
        this.customers = data.data;
      } catch (error) {
        console.error('Error fetching customers:', error);
      } finally {
        this.loadingCustomers = false;
      }
    },
    
    filterAccountCustomers() {
      if (!this.accountSearchQuery) {
        this.filteredAccountCustomers = [];
        return;
      }
      
      const query = this.accountSearchQuery.toLowerCase();
      this.filteredAccountCustomers = this.customers.filter(customer => 
        customer.phone.toLowerCase().includes(query) ||
        customer.name.toLowerCase().includes(query)
      );
    },
    
    selectAccountCustomer(customer) {
      this.selectedAccountCustomer = customer;
      this.showAccountResults = false;
      this.applyCustomerBalance();
    },
    
    clearAccountSearch() {
      this.accountSearchQuery = '';
      this.selectedAccountCustomer = null;
      this.filteredAccountCustomers = [];
      this.calculateBalance();
    },
    
    handleAccountInputBlur() {
      setTimeout(() => {
        this.showAccountResults = false;
      }, 200);
    },
    
    applyCustomerBalance() {
      if (!this.selectedAccountCustomer) {
        return;
      }
      
      const customerBalance = parseFloat(this.selectedAccountCustomer.balance) || 0;
      
      if (customerBalance >= this.grandTotal) {
        this.paidAmount = this.grandTotal;
        this.balance = 0;
        this.cashAmount = '';
        this.creditAmount = '';
      } else if (customerBalance > 0) {
        const remaining = this.grandTotal - customerBalance;
        this.paidAmount = customerBalance;
        
        const cash = parseFloat(this.cashAmount) || 0;
        const credit = parseFloat(this.creditAmount) || 0;
        
        if (cash > 0 || credit > 0) {
          if (cash + credit >= remaining) {
            this.balance = 0;
          } else {
            this.balance = remaining - cash - credit;
          }
        } else {
          this.balance = remaining;
        }
      } else {
        this.calculateBalance();
      }
    },
    
    calculateBalance() {
      const cash = parseFloat(this.cashAmount) || 0;
      const credit = parseFloat(this.creditAmount) || 0;
      const previous = parseFloat(this.previousPayment) || 0;
      
      this.paidAmount = cash + credit;
      
      const customerBalance = this.selectedAccountCustomer ? 
        (parseFloat(this.selectedAccountCustomer.balance) || 0) : 0;
      
      if (customerBalance > 0) {
        const balanceUsed = Math.min(customerBalance, this.grandTotal);
        const remainingAfterBalance = this.grandTotal - balanceUsed;
        this.balance = remainingAfterBalance - this.paidAmount - previous;
      } else {
        this.balance = -this.grandTotal + this.paidAmount + previous + credit;
      }
    },
    
    formatCurrency(value) {
      return parseFloat(value || 0).toFixed(2);
    },
    
    clearInput(field) {
      this[field] = '';
      this.calculateBalance();
    },
    
    // async processPayment() {
    //   try {
    //     const payments = [];
    //     let paymentType = '';
        
    //     // Determine payment type based on inputs
    //     const cashAmount = parseFloat(this.cashAmount) || 0;
    //     const creditAmount = parseFloat(this.creditAmount) || 0;
    //     const hasCustomer = !!this.selectedAccountCustomer;
    //     const previousPayment = parseFloat(this.previousPayment) || 0;
        
    //     if (cashAmount > 0 && creditAmount > 0 && hasCustomer) {
    //       paymentType = "Mixed";
    //     } else if (cashAmount > 0 && creditAmount > 0) {
    //       paymentType = "Mixed";
    //     } else if (cashAmount > 0 && hasCustomer) {
    //       paymentType = "Mixed";
    //     } else if (creditAmount > 0 && hasCustomer) {
    //       paymentType = "Mixed";
    //     } else if (cashAmount > 0) {
    //       paymentType = "Cash";
    //     } else if (creditAmount > 0) {
    //       paymentType = "Credit Card";
    //     } else if (hasCustomer) {
    //       paymentType = "Balance";
    //     } else {
    //       // throw new Error("Please enter payment details");
    //       paymentType = "Mixed";
    //     }
    //     console.log(paymentType)
    //     // Customer balance payment if available
    //     if (this.selectedAccountCustomer && this.selectedAccountCustomer.balance > 0) {
    //       const balanceUsed = Math.min(this.selectedAccountCustomer.balance, this.grandTotal);
    //       if (balanceUsed > 0) {
    //         payments.push({
    //           paymentType: "Balance",
    //           amount: balanceUsed
    //         });
    //       }
    //     }
        
    //     // Cash payment if entered
    //     if (cashAmount > 0) {
    //       payments.push({
    //         paymentType: "Cash",
    //         amount: cashAmount
    //       });
    //     }
        
    //     // Credit payment if entered
    //     if (creditAmount > 0) {
    //       payments.push({
    //         paymentType: "Credit Card",
    //         amount: creditAmount
    //       });
    //     }
        
    //     // Previous payment if entered
    //     if (previousPayment > 0) {
    //       payments.push({
    //         paymentType: "Balance",
    //         amount: previousPayment
    //       });
    //     }

    //     const paymentData = {
    //       customerId: this.selectedAccountCustomer?.id || null,
    //       paymentType: paymentType,
    //       payments: payments
    //     };

    //     const token = localStorage.getItem('authToken');
    //     if (!token) {
    //       throw new Error('Authentication token not found');
    //     }

    //     const response = await fetch(`${this.$BASE_API_URL}/api/orders/pay/${this.orderId}`, {
    //       method: 'POST',
    //       headers: {
    //         'Content-Type': 'application/json',
    //         'Authorization': `Bearer ${token}`
    //       },
    //       body: JSON.stringify(paymentData)
    //     });

    //     if (!response.ok) {
    //       const errorData = await response.json();
    //       throw new Error(errorData.message || 'Payment failed');
    //     }

    //     this.$emit('payment-success');
    //     this.closeModal();

    //   } catch (error) {
    //     console.error('Payment error:', error);
    //     this.$emit('payment-error', error.message);
    //   }
    // },
    async processPayment() {
  try {
    const payments = [];
    const cashAmount = parseFloat(this.cashAmount) || 0;
    const creditAmount = parseFloat(this.creditAmount) || 0;
    const hasCustomer = !!this.selectedAccountCustomer;
    const previousPayment = parseFloat(this.previousPayment) || 0;
    const totalPaid = cashAmount + creditAmount + (hasCustomer ? (parseFloat(this.selectedAccountCustomer.balance) || 0) : 0) + previousPayment;
    const remainingAmount = this.grandTotal - totalPaid;

    // 1. If only cash amount entered
    if (cashAmount > 0 && creditAmount === 0) {
      payments.push({
        paymentType: "Cash",
        amount: cashAmount
      });
    }

    // 2. If only credit amount entered
    if (creditAmount > 0 && cashAmount === 0) {
      payments.push({
        paymentType: "Credit Card",
        amount: creditAmount
      });
    }

    // 3. If both cash and credit entered
    if (cashAmount > 0 && creditAmount > 0) {
      payments.push(
        {
          paymentType: "Cash",
          amount: cashAmount
        },
        {
          paymentType: "Credit Card",
          amount: creditAmount
        }
      );
    }

    // 4. If customer selected and remaining amount exists
    if (hasCustomer && remainingAmount > 0) {
      payments.push({
        paymentType: "Balance",
        amount: remainingAmount
      });
    }

    // 5. If previous payment exists
    if (previousPayment > 0) {
      payments.push({
        paymentType: "Balance",
        amount: previousPayment
      });
    }

    // 6. If only customer balance is used (no cash/credit)
    if (hasCustomer && cashAmount === 0 && creditAmount === 0 && previousPayment === 0) {
      const balanceUsed = Math.min(this.selectedAccountCustomer.balance, this.grandTotal);
      if (balanceUsed > 0) {
        payments.push({
          paymentType: "Balance",
          amount: balanceUsed
        });
      }
    }

    // Prepare payment data
    const paymentData = {
      customerId: this.selectedAccountCustomer?.id || null,
      payments: payments
    };

    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${this.$BASE_API_URL}/api/orders/pay/${this.orderId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(paymentData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Payment failed');
    }

    this.$emit('payment-success');
    this.closeModal();

  } catch (error) {
    console.error('Payment error:', error);
    this.$emit('payment-error', error.message);
  }
},
    closeModal() {
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
/* Existing styles remain the same, just adding new ones for the keypad button */
.input-group-append .btn-keypad {
  border-color: #ced4da;
  background-color: #f8f9fa;
}

.input-group-append .btn-keypad:hover {
  background-color: #e9ecef;
}

.input-group-append .btn-keypad svg {
  width: 16px;
  height: 16px;
}

.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}
.modal {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1050;
    display: none;
    width: 100%;
    /* height: 111%; */
    height: 100%;
    overflow-y: scroll;
}

.modal-dialog {
  max-width: 800px;
  margin: 1.75rem auto;
}

.modal-content {
  border: none;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.modal-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.25rem 1.5rem;
}

.modal-title {
  font-weight: 600;
  font-size: 1.25rem;
}

.modal-body {
  padding: 0 1.5rem;
}

.modal-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 0 1rem 1.5rem;
  justify-content: space-between;
}

.section-title {
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.submit-btn{
      background-color: #45a049;
      color: #fff;
}
.total-section {
  background-color: #f8f9fa;
  border-left: 4px solid #4caf50;
}

.input-group-text {
  background-color: #f8f9fa;
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

.dropdown-results {
  position: absolute;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.list-group-item {
  border-left: none;
  border-right: none;
  padding: 0.75rem 1.25rem;
  cursor: pointer;
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:last-child {
  border-bottom: none;
}

.customer-info {
  border-left: 4px solid;
}

.bg-success-light {
  background-color: rgba(40, 167, 69, 0.1);
  border-left-color: #28a745;
}

.bg-danger-light {
  background-color: rgba(220, 53, 69, 0.1);
  border-left-color: #dc3545;
}

.btn-lg {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  line-height: 1.5;
  border-radius: 0.375rem;
}

.btn-outline-secondary:hover {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}
.btn.btn-lg.btn-outline-secondary{
  background-color: #E91C0C;
  color: #fff;
}
.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.font-weight-bold {
  font-weight: 600 !important;
}

.rounded {
  border-radius: 0.375rem !important;
}

.close {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
  opacity: 1;
}

.close:hover {
  opacity: 0.75;
}
/* Add to your style section */
.numeric-keypad-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1100; /* Higher than modal z-index (1050) */
  background: white;
  padding: 15px;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
}
/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-dialog {
    margin: 0.5rem auto;
  }
  
  .modal-body {
    padding: 1rem;
  }
}
</style>